/**
 * Loading Components - Consolidated loading indicators
 *
 * Combines simple CSS animations and Framer Motion animations in one place.
 *
 * **SIMPLE VARIANTS** (CSS only - lightweight):
 * - LoadingSpinner: Classic spinning circle
 * - LoadingDots: Three pulsing dots
 * - LoadingSkeleton: Pulse animation for placeholders
 * - LoadingCard: Skeleton card layout
 *
 * **ANIMATED VARIANTS** (Framer Motion - smooth):
 * - LoadingSpinnerAnimated: Smooth rotating spinner
 * - LoadingDotsAnimated: Bouncing dots with stagger
 * - PulseAnimation: Wrapper for pulse effect
 *
 * **WHEN TO USE:**
 * - Simple: Fast loads, no complex animations needed
 * - Animated: Smoother experience, worth the bundle size
 */

import React from 'react'
import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'

// ============================================================================
// SIMPLE VARIANTS (CSS only)
// ============================================================================

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg' | 'xl'
  className?: string
  text?: string
}

const sizeClasses = {
  sm: 'h-4 w-4',
  md: 'h-6 w-6',
  lg: 'h-8 w-8',
  xl: 'h-12 w-12'
}

/**
 * LoadingSpinner - Classic spinning circle (CSS animation)
 *
 * Lightweight, no dependencies. Use for simple loading states.
 */
export function LoadingSpinner({ size = 'md', className, text }: LoadingSpinnerProps) {
  return (
    <div className="flex flex-col items-center justify-center space-y-2">
      <div
        className={cn(
          'animate-spin rounded-full border-2 border-muted border-t-primary',
          sizeClasses[size],
          className
        )}
      />
      {text && (
        <p className="text-sm text-muted-foreground">{text}</p>
      )}
    </div>
  )
}

/**
 * LoadingDots - Three pulsing dots (CSS animation)
 *
 * Good for inline loading states (e.g., "Loading...")
 */
export function LoadingDots({ className }: { className?: string }) {
  return (
    <div className={cn('flex space-x-1', className)}>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="h-2 w-2 bg-primary rounded-full animate-pulse"
          style={{
            animationDelay: `${i * 0.1}s`,
            animationDuration: '0.6s'
          }}
        />
      ))}
    </div>
  )
}

/**
 * LoadingSkeleton - Pulse animation for content placeholders
 *
 * Use for skeleton screens while content loads.
 */
export function LoadingSkeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'animate-pulse rounded-md bg-muted',
        className
      )}
    />
  )
}

/**
 * LoadingCard - Pre-built skeleton card layout
 *
 * Quick skeleton for card-based layouts.
 */
export function LoadingCard() {
  return (
    <div className="p-6 space-y-4">
      <LoadingSkeleton className="h-4 w-3/4" />
      <LoadingSkeleton className="h-4 w-1/2" />
      <LoadingSkeleton className="h-20 w-full" />
      <div className="flex space-x-2">
        <LoadingSkeleton className="h-6 w-16" />
        <LoadingSkeleton className="h-6 w-16" />
      </div>
    </div>
  )
}

// ============================================================================
// ANIMATED VARIANTS (Framer Motion)
// ============================================================================

const spinnerVariants = {
  animate: {
    rotate: 360,
    transition: {
      duration: 1,
      repeat: Infinity,
      ease: "linear"
    }
  }
}

const dotsVariants = {
  animate: {
    scale: [1, 1.2, 1],
    transition: {
      duration: 0.6,
      repeat: Infinity,
      repeatDelay: 0.1
    }
  }
}

const pulseVariants = {
  animate: {
    scale: [1, 1.05, 1],
    opacity: [0.7, 1, 0.7],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: "easeInOut"
    }
  }
}

interface LoadingSpinnerAnimatedProps {
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

/**
 * LoadingSpinnerAnimated - Smooth rotating spinner (Framer Motion)
 *
 * Smoother than CSS version, use for prominent loading states.
 */
export function LoadingSpinnerAnimated({ size = 'md', className }: LoadingSpinnerAnimatedProps) {
  const animatedSizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8'
  }

  return (
    <motion.div
      variants={spinnerVariants}
      animate="animate"
      className={cn(
        animatedSizeClasses[size],
        'border-2 border-primary border-t-transparent rounded-full',
        className
      )}
    />
  )
}

/**
 * LoadingDotsAnimated - Bouncing dots with stagger (Framer Motion)
 *
 * More playful than CSS version, use for delightful loading states.
 */
export function LoadingDotsAnimated({ className }: { className?: string }) {
  return (
    <div className={cn('flex space-x-1', className)}>
      {[0, 1, 2].map((i) => (
        <motion.div
          key={i}
          variants={dotsVariants}
          animate="animate"
          style={{ animationDelay: `${i * 0.1}s` }}
          className="w-2 h-2 bg-primary rounded-full"
        />
      ))}
    </div>
  )
}

/**
 * PulseAnimation - Wrapper for pulse effect (Framer Motion)
 *
 * Wraps any component with a subtle pulse animation.
 * Good for drawing attention to important elements.
 */
export function PulseAnimation({ children, className }: { children: React.ReactNode, className?: string }) {
  return (
    <motion.div
      variants={pulseVariants}
      animate="animate"
      className={className}
    >
      {children}
    </motion.div>
  )
}

// ============================================================================
// DEFAULT EXPORTS (for backward compatibility)
// ============================================================================

export default LoadingSpinner
